<script setup lang="ts">
import { onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { App } from "@xlabmedical/medic-web-framework";
import { onKeyStroke } from "@vueuse/core";

import { useWebsocket } from "@/composables/WebRgWebsocketComposable";
import {
    WebRgWSChatEvent,
    WebRgWSJobStatusUpdatedEvent,
    WebRgWSProjectFileAddedEvent,
    WebRgWSProjectFileDeletedEvent,
    WebRgWSProjectFileUpdatedEvent,
    WebRgWSProjectUpdatedEvent,
    WebRgWSProjectAddedEvent,
    WebRgWSWeblinkOrderCreatedEvent,
    WebRgWSWeblinkOrderUpdatedEvent,
} from "@/util/WebRgWebsocketManager";
import { storeToRefs } from "pinia";
import { usePatientsStore } from "@/stores/PatientsStore";
import { FileElement, ProjectFileType } from "@xlabmedical/medic-api";
import { isProjectMobilePreviewFile } from "./RgProjectFileEventUtil";

const router = useRouter();
const route = useRoute();
const patientsStore = usePatientsStore();
const { patients, projectsModel } = storeToRefs(patientsStore);

onKeyStroke("1", (e) => {
    if (e.ctrlKey) {
        router.push("/patients");
    }
});

onMounted(async () => {
    await patientsStore.ensureVendorName();
});

type ProjectFileEvent = WebRgWSProjectFileAddedEvent | WebRgWSProjectFileUpdatedEvent;

const objectTypes = [ProjectFileType.STL, ProjectFileType.Document, ProjectFileType.Image];
const ignoredTypes = new Set([ProjectFileType.ProjectAttachmentPreview, "stl_preview", ProjectFileType.ProcessingData]);
const createdOrderRefreshDelays = [0, 300, 900];

const waitForCreatedOrderRefresh = (delay: number) => new Promise<void>((resolve) => window.setTimeout(resolve, delay));

const isCreatedOrderLoaded = (projectId: number) => projectsModel.value.some((project) => Number(project.weblinkOrderData?.projectId) === Number(projectId));

const resolveMobileFile = (event: ProjectFileEvent, eventFile: FileElement) => {
    const isMobileFile = (file: FileElement) => file.type === ProjectFileType.Mobile || (event.type === "PROJECT_FILE_UPDATED" && isProjectMobilePreviewFile(file));

    if (isMobileFile(eventFile)) {
        return eventFile;
    }

    const files = event.data?.files ?? [];
    if (eventFile.name?.endsWith(".zip")) {
        const mobileName = `${eventFile.name}.mobile.json`;
        const exactMatch = files.find((file) => isMobileFile(file) && file.name === mobileName);
        if (exactMatch) {
            return exactMatch;
        }
    }

    const mobileFiles = files.filter(isMobileFile);
    if (mobileFiles.length === 1) {
        return mobileFiles[0];
    }

    return undefined;
};

useWebsocket({
    chatMessageEvent: async (event: WebRgWSChatEvent) => {
        await patientsStore.updatePatientNotifications(event);
        await patientsStore.updatePatientListNotifications();
    },
    orderCreatedEvent: async (event: WebRgWSWeblinkOrderCreatedEvent) => {
        await patientsStore.updateSinglePatient(event);

        const { originProjectReference } = event.data;
        const createdOrderProjectId = event.projectId || event.data.id;

        let patient = patients.value.find(
            (p) => p.patientName === originProjectReference || Number(p.orderProjectId) === Number(createdOrderProjectId) || Number(p.orderProjectId) === Number(event.data.id),
        );

        if (!patient) {
            await patientsStore.loadPatientsSlim();
            patient = patients.value.find(
                (p) => p.patientName === originProjectReference || Number(p.orderProjectId) === Number(createdOrderProjectId) || Number(p.orderProjectId) === Number(event.data.id),
            );
        }

        if (patient?.patientId) {
            // The order-created message can arrive before its attachments are returned by /full.
            // Retry the refresh briefly so the card gets weblinkOrderData and renders in its order panel.
            for (const delay of createdOrderRefreshDelays) {
                if (delay) await waitForCreatedOrderRefresh(delay);

                await patientsStore.loadPatientFullByIdSlim(Number(patient.patientId), true);

                if (isCreatedOrderLoaded(createdOrderProjectId)) break;
            }

            const projectsToLoad = projectsModel.value.filter((p) => !p.imageBuffer && p.projectDirty === true && p.patientId === patient.patientId);

            await Promise.all(projectsToLoad.map((project) => patientsStore.loadProjectDataSlim(project.id), true));
        }
    },
    orderUpdatedEvent: async (event: WebRgWSWeblinkOrderUpdatedEvent) => {
        await patientsStore.updateSinglePatientOrder(event);
    },
    projectFileAddedEvent: async (event: WebRgWSProjectFileAddedEvent) => {
        if (!event.data) {
            console.warn("Invalid event data PROJECT_FILE_ADDED: ", event);
            return;
        }

        const { id } = event.data;

        const patient = patients.value.find((p) => p.patientId === id.toString());

        if (!patient) {
            await patientsStore.loadPatientsSlim();
        }

        const eventFile = event.data.files?.find((f) => f.id === event.projectFileId);

        if (!eventFile) {
            console.warn("Invalid event data PROJECT_FILE_ADDED (missing file): ", event);
            return;
        }

        if (ignoredTypes.has(eventFile.type)) {
            console.warn("Invalid event data PROJECT_FILE_ADDED: ", event);
            return;
        }

        if (objectTypes.includes(eventFile.type)) {
            await patientsStore.addObjectFromWs(eventFile, event);
        } else {
            const mobileFile = resolveMobileFile(event, eventFile);
            if (!mobileFile) {
                console.warn("Invalid event data PROJECT_FILE_ADDED (missing mobile file): ", event);
                return;
            }
            await patientsStore.addProjectFromWS(mobileFile, event);
        }
    },
    projectFileUpdatedEvent: async (event: WebRgWSProjectFileUpdatedEvent) => {
        if (!event.data) {
            console.warn("Invalid event data PROJECT_FILE_UPDATED: ", event);
            return;
        }

        const eventFile = event.data.files?.find((f) => f.id === event.projectFileId);

        if (!eventFile) {
            console.warn("Invalid event data PROJECT_FILE_UPDATED (missing file): ", event);
            return;
        }

        if (ignoredTypes.has(eventFile.type) && !isProjectMobilePreviewFile(eventFile)) {
            console.warn("Invalid event data PROJECT_FILE_UPDATED 1: ", event);
            return;
        }

        if (objectTypes.includes(eventFile.type)) {
            await patientsStore.addObjectFromWs(eventFile, event);
        } else {
            const mobileFile = resolveMobileFile(event, eventFile);
            if (!mobileFile) {
                console.warn("Invalid event data PROJECT_FILE_UPDATED (missing mobile file): ", event);
                return;
            }
            await patientsStore.addProjectFromWS(mobileFile, event);
        }
    },
    projectFileDeletedEvent: async (event: WebRgWSProjectFileDeletedEvent) => {
        const deletedId = Number(event.projectFileId);
        let index = projectsModel.value.findIndex((f) => Number(f.projectFileId) === deletedId || Number(f.id) === deletedId);
        if (index !== -1) {
            projectsModel.value.splice(index, 1);
            if (patientsStore.selectedPatientProjects?.length) {
                const selectedIndex = patientsStore.selectedPatientProjects.findIndex((f) => Number(f.projectFileId) === deletedId || Number(f.id) === deletedId);
                if (selectedIndex !== -1) {
                    patientsStore.selectedPatientProjects.splice(selectedIndex, 1);
                }
            }
            return;
        }

        const removeById = <T extends { id?: number; projectFileId?: number }>(arr: T[]) => {
            const next = arr.filter((f) => Number(f.id) !== deletedId && Number(f.projectFileId) !== deletedId);
            if (next.length !== arr.length) {
                arr.splice(0, arr.length, ...next);
            }
        };

        removeById(patientsStore.projectFileModel);
        removeById(patientsStore.attachmentsFileModel);
        removeById(patientsStore.selectedPatientFileModel);
    },
    projectUpdatedEvent: async (event: WebRgWSProjectUpdatedEvent) => {
        await patientsStore.updateSingleProject(event);
    },
    projectAddedEvent: async (event: WebRgWSProjectAddedEvent) => {
        await patientsStore.loadPatientsSlim();

        const eventPatientId = Number(event.data?.id);
        const eventPatientUid = event.data?.uid;
        const routePatientId = Array.isArray(route.params.id) ? route.params.id[0] : route.params.id;
        const selectedPatientId = Number(patientsStore.selectedPatientFull?.id);
        const selectedPatientUid = patientsStore.selectedPatientFull?.uuid;
        const patientFromUid = eventPatientUid ? patients.value.find((patient) => patient.uuid === eventPatientUid || patient.patientName === eventPatientUid) : undefined;
        const refreshPatientId = Number.isFinite(eventPatientId) ? eventPatientId : Number(patientFromUid?.patientId ?? selectedPatientId);
        const matchesSelectedPatient =
            (Number.isFinite(eventPatientId) && selectedPatientId === eventPatientId) ||
            (!!eventPatientUid && (routePatientId === eventPatientUid || selectedPatientUid === eventPatientUid));

        if (matchesSelectedPatient && Number.isFinite(refreshPatientId)) {
            await patientsStore.loadPatientFullByIdSlim(refreshPatientId, true);
        }
    },
    unzippedProjectFilesUpdatedEvent: async () => {
        return;
    },
    jobStatusUpdatedEvent: async (event: WebRgWSJobStatusUpdatedEvent) => {
        const status = SegmentationStatus[event.data.status as keyof typeof SegmentationStatus] ?? SegmentationStatus.UNKNOWN;

        await patientsStore.updateProjectSegmentationStatus(event.data.projectFileUuid, status);
    },
});
</script>

<template>
    <div>
        <App />
    </div>
</template>

<style scoped></style>
